import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs, uploadFile } from "./client";
import {
  scopeKey,
  type Artwork,
  type ArtworkPin,
  type AuditEntry,
  type CoverlessScope,
  type PublishStatus,
  type Role,
  type ScheduledEvent,
  type Segment,
  type Setting,
  type User,
} from "./types";
import type { NativeFile } from "../platform/native";

export interface SegmentPatch {
  start?: string;
  end?: string;
  title?: string | null;
  djs?: string | null;
  guests?: string | null;
  artist?: string | null;
  marker?: string | null;
  city?: string | null;
  region?: string | null;
  country?: string | null;
  event_id?: number | null;
  show_id?: number | null;
  starred?: boolean;
  artwork_id?: number | null;
}

/**
 * Every write to a segment goes through here.
 *
 * One mutation rather than one per field, because the server's PATCH is already partial and
 * the invalidation is the same either way: a changed span moves a block on the timeline, and
 * a changed title moves a row in the list.
 */
export function useUpdateSegment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: SegmentPatch }) =>
      api.patch<Segment>(`/api/segments/${id}`, patch),
    onSuccess: (segment) => {
      client.setQueryData(["segment", segment.id], segment);
      invalidateSegments(client);
    },
  });
}

export function useSegmentAction(action: "approve" | "unapprove" | "reset" | "unarchive") {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<Segment>(`/api/segments/${id}/${action}`),
    onSuccess: (segment) => {
      client.setQueryData(["segment", segment.id], segment);
      invalidateSegments(client);
    },
  });
}

export function useStarSegment() {
  const client = useQueryClient();
  return useMutation({
    // Starring is the only thing standing between a jukebox track and tombstoning, so it is
    // its own call rather than a field on the patch: the server audits it as a decision.
    mutationFn: ({ id, starred }: { id: number; starred: boolean }) =>
      api.post<Segment>(`/api/segments/${id}/star${qs({ starred })}`),
    onSuccess: (segment) => {
      client.setQueryData(["segment", segment.id], segment);
      invalidateSegments(client);
    },
  });
}

export function usePublishSegment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, repost }: { id: number; repost: boolean }) =>
      api.post<Segment>(`/api/segments/${id}/publish${qs({ repost })}`),
    onSuccess: (segment) => {
      client.setQueryData(["segment", segment.id], segment);
      invalidateSegments(client);
    },
  });
}

export function useSplitSegment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, at }: { id: number; at: string }) =>
      api.post<Segment[]>(`/api/segments/${id}/split`, { at }),
    onSuccess: () => invalidateSegments(client),
  });
}

/**
 * Play a show and the next one as one show (`join-next`), or take that back (`unjoin`).
 *
 * Neither moves a row, so there is nothing to reconcile beyond what the panel is painting.
 * Every `["segment", id]` entry goes, not just the one that came back: a group is dissolved
 * from any of its sections, and the ones that were not returned are exactly the ones whose
 * chips would otherwise still offer to unjoin an already-unjoined show.
 */
export function useSegmentJoin(action: "join-next" | "unjoin") {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<Segment>(`/api/segments/${id}/${action}`, {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["segment"] });
      invalidateSegments(client);
    },
  });
}

export function useDeleteSegment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<void>(`/api/segments/${id}`),
    onSuccess: () => invalidateSegments(client),
  });
}

export function useSplitProposal(id: number | null) {
  return useQuery({
    queryKey: ["split-proposal", id],
    queryFn: () =>
      api.get<{ segment_id: number; boundaries: string[]; event_ids: number[] } | null>(
        `/api/segments/${id}/split-proposal`,
      ),
    enabled: id !== null,
  });
}

export function useArtworkLibrary() {
  return useQuery({
    queryKey: ["artwork", "library"],
    queryFn: () => api.get<Artwork[]>("/api/artwork"),
    // An editor uploads, picks, and uploads again; a stale grid is a missing thumbnail.
    staleTime: 0,
  });
}

export function useUploadArtwork() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (file: File | NativeFile) => uploadFile<Artwork>("/api/artwork", file),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["artwork", "library"] }),
  });
}

export function usePinArtwork() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, artworkId }: { id: number; artworkId: number }) =>
      api.post<{ segment: Segment; updated: number }>(`/api/segments/${id}/artwork/pin`, {
        artwork_id: artworkId,
      }),
    onSuccess: ({ segment }) => {
      client.setQueryData(["segment", segment.id], segment);
      invalidateSegments(client);
    },
  });
}

export function useUnpinArtwork() {
  const client = useQueryClient();
  return useMutation({
    // A pin covers every airing of the series, so withdrawing it moves rows this cache has
    // no other reason to refetch -- the blanket segment invalidation is the point.
    mutationFn: (id: number) =>
      api.del<{ segment: Segment; updated: number }>(`/api/segments/${id}/artwork/pin`),
    onSuccess: ({ segment }) => {
      client.setQueryData(["segment", segment.id], segment);
      invalidateSegments(client);
    },
  });
}

export function useArtworkPins() {
  return useQuery({
    queryKey: ["artwork", "pins"],
    queryFn: () => api.get<ArtworkPin[]>("/api/artwork/pins"),
  });
}

/** Scopes whose airings have no cover, most coverless airings first. */
export function useCoverlessScopes() {
  return useQuery({
    queryKey: ["artwork", "needed"],
    queryFn: () => api.get<CoverlessScope[]>("/api/artwork/pins/needed"),
  });
}

/** The captured schedule, searched by billing, title or city. Idle until something is typed. */
export function useScheduleSearch(q: string) {
  return useQuery({
    queryKey: ["events", "search", q],
    queryFn: () => api.get<ScheduledEvent[]>(`/api/events${qs({ q, limit: 100 })}`),
    enabled: q.trim().length > 0,
  });
}

export function useWriteArtworkPin() {
  const client = useQueryClient();
  return useMutation({
    // Exactly one of show_id / djs, which is what the server validates. A null artwork_id
    // withdraws the pin.
    mutationFn: (body: { show_id?: number; djs?: string; artwork_id: number | null }) =>
      api.put<{ pin: ArtworkPin | null; updated: number }>("/api/artwork/pins", body),
    // The list is maintained from the response rather than refetched. The API commits its
    // session in dependency teardown, after the response has been sent, so a refetch fired
    // from here races that commit and can land the rows as they were *before* this write --
    // measured: a GET issued the instant the PUT resolves still reports the old list. The
    // response is the stored pin, so it is both authoritative and already paid for.
    onSuccess: ({ pin }, body) => {
      const written = pin
        ? scopeKey(pin.show_id, pin.dj_key)
        : scopeKey(body.show_id ?? null, body.djs ?? null);
      client.setQueryData<ArtworkPin[]>(["artwork", "pins"], (old) => [
        ...(pin ? [pin] : []),
        ...(old ?? []).filter((p) => scopeKey(p.show_id, p.dj_key) !== written),
      ]);
      // A pin spreads a cover across every airing in scope, so the lists are stale too.
      invalidateSegments(client);
    },
  });
}

export function useEvents(startIso: string | null, endIso: string | null) {
  return useQuery({
    queryKey: ["events", startIso, endIso],
    queryFn: () => api.get<ScheduledEvent[]>(`/api/events${qs({ start: startIso, end: endIso })}`),
    enabled: startIso !== null && endIso !== null,
  });
}

export function useBoundaries(startIso: string | null, endIso: string | null) {
  return useQuery({
    queryKey: ["boundaries", startIso, endIso],
    queryFn: () =>
      api.get<{ icy: string[]; scheduled: string[]; truncated: boolean }>(
        `/api/timeline/boundaries${qs({ start: startIso, end: endIso })}`,
      ),
    enabled: startIso !== null && endIso !== null,
    staleTime: 60_000,
  });
}

export function useSettings() {
  return useQuery({
    queryKey: ["settings", "all"],
    queryFn: () => api.get<Setting[]>("/api/settings"),
  });
}

/** Who each destination is signed in as, and why it cannot post. */
export function usePublishStatus() {
  return useQuery({
    queryKey: ["publish", "status"],
    queryFn: () => api.get<PublishStatus[]>("/api/publish/status"),
  });
}

export function useUpdateSetting() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      api.put<Setting>(`/api/settings/${key}`, { value }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["settings"] });
      void client.invalidateQueries({ queryKey: ["settings", "public"] });
      // Segmentation thresholds change what the next worker pass produces, but the station
      // zone changes what this page renders right now.
      invalidateSegments(client);
    },
  });
}

export function useResetSetting() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => api.del<Setting>(`/api/settings/${key}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["settings"] });
      void client.invalidateQueries({ queryKey: ["settings", "public"] });
    },
  });
}

export function useAudit(filters: { entity?: string; action?: string; limit?: number } = {}) {
  const query = qs({ ...filters, limit: filters.limit ?? 100 });
  return useQuery({
    queryKey: ["audit", query],
    queryFn: () => api.get<AuditEntry[]>(`/api/audit${query}`),
  });
}

export function useUsers() {
  return useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<User[]>("/api/users"),
  });
}

/**
 * Fold a row the server just returned into the cached list.
 *
 * Used instead of invalidating, because every one of these mutations answers with the
 * authoritative row. Re-fetching the whole list to learn what the response already said
 * costs a round trip and, worse, races: the refetch and the mutation response are two
 * independent answers to "what is this user now", and the list is ordered by id, so the
 * one that lands last wins rather than the one that is right.
 */
function mergeUser(client: QueryClient, user: User): void {
  client.setQueryData<User[]>(["users"], (rows) => {
    if (!rows) return rows;
    const known = rows.some((row) => row.id === user.id);
    if (known) return rows.map((row) => (row.id === user.id ? user : row));
    // New account: the server orders by id and a new row always has the highest one.
    return [...rows, user];
  });
}

export function useCreateUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { username: string; email?: string; password: string; role: Role }) =>
      api.post<User>("/api/users", body),
    onSuccess: (user) => mergeUser(client, user),
  });
}

export function useUpdateUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number; role?: Role; active?: boolean }) =>
      api.patch<User>(`/api/users/${id}`, patch),
    onSuccess: (user) => {
      mergeUser(client, user);
      // Changing your own role changes what the rest of the app will let you do, and
      // deactivating anyone cuts their sessions server-side.
      void client.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useDeleteUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<void>(`/api/users/${id}`),
    onSuccess: async (_, id) => {
      await client.cancelQueries({ queryKey: ["users"] });
      client.setQueryData<User[]>(["users"], (rows) => rows?.filter((user) => user.id !== id));
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useSetUserPassword() {
  return useMutation({
    // Nothing cached describes a password, so there is nothing to update.
    mutationFn: ({ id, password }: { id: number; password: string }) =>
      api.post<void>(`/api/users/${id}/password`, { new_password: password }),
  });
}

export function useResendUserConfirmation() {
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ detail: string }>(`/api/users/${id}/resend-confirmation`),
  });
}

export function useConfirmUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<User>(`/api/users/${id}/confirm`),
    onSuccess: (user) => mergeUser(client, user),
  });
}

function invalidateSegments(client: QueryClient): void {
  void client.invalidateQueries({ queryKey: ["segments"] });
  // Every zoom level is its own cache entry (the key carries the window and the width), so
  // invalidating alone leaves the levels nobody is looking at holding pre-edit blocks. The
  // next zoom mounts one of them, react-query paints the cached answer first and refetches
  // after -- which is the frame where a moved show start snaps back to where it was. Drop
  // them instead: the canvas keeps drawing the last window until the new one lands, so
  // there is nothing to flash.
  void client.removeQueries({ queryKey: ["timeline"], type: "inactive" });
  void client.invalidateQueries({ queryKey: ["timeline"] });
  void client.invalidateQueries({ queryKey: ["nowplaying"] });
  // Any write that moves a cover changes which scopes still need a pin. Only the Covers page
  // mounts that query, so everywhere else this costs a cache mark and no request.
  void client.invalidateQueries({ queryKey: ["artwork", "needed"] });
}
